// [2026-02-07] style-extraction: moved inline styles to SCSS module
import React, { useEffect, useRef, useState } from "react";
import { useUserSearch } from "./useUserSearch";
import { AdminUserLite } from "../../services/adminUsers";
import styles from "./Inventory.module.scss";

type Props = {
  value?: AdminUserLite | null;
  onChange: (u: AdminUserLite | null) => void;
  placeholder?: string;
  autoFocus?: boolean;
};

export default function UserAutocomplete({ value, onChange, placeholder, autoFocus }: Props) {
  const { term, setTerm, items, loading, error, canSearch, page, totalPages, loadMore } =
    useUserSearch();
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onDocClick = (e: MouseEvent) => {
      if (!wrapRef.current) return;
      if (!wrapRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  const renderLabel = (u: AdminUserLite) => {
    const name = [u.firstName, u.lastName].filter(Boolean).join(" ").trim();
    const left = name || u.email || u.companyName || u._id;
    const right = u.email && left !== u.email ? ` • ${u.email}` : "";
    return `${left}${right}`;
  };

  return (
    <div ref={wrapRef} className="position-relative">
      <input
        className="form-control"
        placeholder={placeholder || "Search by name or email"}
        value={value ? renderLabel(value) : term}
        onChange={(e) => {
          onChange(null);
          setTerm(e.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        autoFocus={autoFocus}
      />
      {open && (
        <div className={`dropdown-menu show w-100 ${styles.dropdownScrollable}`}>
          {!canSearch && (
            <span className="dropdown-item-text text-muted">Type at least 2 characters…</span>
          )}
          {error && <span className="dropdown-item-text text-danger">{error}</span>}
          {canSearch && items.length === 0 && !loading && (
            <span className="dropdown-item-text">No results</span>
          )}
          {items.map((u) => (
            <button
              key={u._id}
              type="button"
              className="dropdown-item"
              onClick={() => {
                onChange(u);
                setOpen(false);
              }}
            >
              {renderLabel(u)}
            </button>
          ))}
          {canSearch && page < totalPages && (
            <button type="button" className="dropdown-item text-center" onClick={loadMore}>
              Load more…
            </button>
          )}
          {loading && <span className="dropdown-item-text text-muted">Loading…</span>}
        </div>
      )}
    </div>
  );
}
